import React from "react";
import LegalPageShell from "./LegalPageShell";

const DEFAULT_BODY = `
<h2>1. Who we are</h2>
<p>Dream Home Engineers (“<strong>we</strong>”, “<strong>us</strong>”, “<strong>our</strong>”) operates the online house-plan marketplace at <a href="https://plans.dreamhomeengineers.com">plans.dreamhomeengineers.com</a> (the “<strong>Site</strong>”). Our registered address is 21-205, Valli Nilayam, Balajinagar, Kukatpally, Hyderabad, Telangana 500072, India.</p>

<h2>2. Scope of this policy</h2>
<p>This Privacy Policy explains what personal data we collect when you browse the Site, purchase a house plan, register an account, submit an enquiry, or subscribe to our newsletter, and how we use, share, and protect that data. By using the Site you agree to the practices described here.</p>

<h2>3. Data we collect</h2>
<h3>3.1 Provided by you</h3>
<ul>
  <li><strong>Account data</strong> — name, email, password (hashed), phone number, billing address.</li>
  <li><strong>Enquiry data</strong> — details you submit in Contact, Custom Design, or plan-modification forms.</li>
  <li><strong>Purchase data</strong> — items purchased, invoice details, order history. Payment card details are handled directly by our payment processors (Stripe, PayPal, Razorpay) and never touch our servers.</li>
  <li><strong>Communications</strong> — correspondence with our support team.</li>
</ul>

<h3>3.2 Collected automatically</h3>
<ul>
  <li><strong>Usage data</strong> — pages viewed, search queries, referring URLs, IP address, browser, device, session duration.</li>
  <li><strong>Cookies</strong> — see Section 8.</li>
  <li><strong>Product-analytics events</strong> — page views and interaction events collected via privacy-respecting analytics (e.g. PostHog).</li>
</ul>

<h2>4. Why we process your data (lawful bases)</h2>
<ul>
  <li><strong>Contract performance</strong> — to fulfill your plan purchase, deliver files, provide customer support.</li>
  <li><strong>Legitimate interests</strong> — securing the Site, preventing fraud, improving the product.</li>
  <li><strong>Consent</strong> — for optional marketing emails, non-essential cookies, product analytics.</li>
  <li><strong>Legal obligation</strong> — tax, accounting, and anti-money-laundering compliance.</li>
</ul>

<h2>5. Sharing your data</h2>
<p>We do not sell personal data. We share limited data with:</p>
<ul>
  <li><strong>Payment processors</strong>: Stripe, PayPal, Razorpay — to process purchases.</li>
  <li><strong>Transactional email</strong>: Resend — to send order confirmations and account emails.</li>
  <li><strong>Fraud / bot protection</strong>: Cloudflare Turnstile — to protect forms.</li>
  <li><strong>Product analytics</strong>: PostHog — to understand product usage.</li>
  <li><strong>Cloud hosting</strong>: Emergent — to run our application and store files.</li>
  <li><strong>Legal &amp; regulatory authorities</strong> — where required by law.</li>
</ul>

<h2>6. International transfers</h2>
<p>We are based in India. When you access the Site from other jurisdictions your data will be transferred to and processed in India and in the cloud regions where our processors operate. We rely on standard contractual clauses and vendor DPAs to safeguard cross-border transfers.</p>

<h2>7. Retention</h2>
<p>We retain account and purchase records for as long as your account is active plus <strong>7 years</strong> to meet tax and accounting obligations. Marketing subscriptions are retained until you unsubscribe. Support and enquiry data are retained for up to <strong>3 years</strong> after the last interaction.</p>

<h2>8. Cookies</h2>
<p>We use strictly-necessary cookies for authentication, cart persistence, and CSRF protection. With your consent we also use analytics cookies to measure product usage. You can control cookies through your browser or our future cookie banner. Blocking essential cookies may break account/cart functionality.</p>

<h2>9. Your rights</h2>
<p>Depending on your jurisdiction (GDPR, CCPA, India DPDPA and similar laws) you may have the right to:</p>
<ul>
  <li>Access a copy of the personal data we hold about you.</li>
  <li>Request correction of inaccurate data.</li>
  <li>Request deletion of your data (subject to legal-retention obligations).</li>
  <li>Object to or restrict processing.</li>
  <li>Withdraw consent for marketing at any time.</li>
  <li>Lodge a complaint with your data-protection authority.</li>
</ul>
<p>Email <a href="mailto:nagesh@dreamhomeengineers.com">nagesh@dreamhomeengineers.com</a> to exercise any of these rights. We will respond within 30 days.</p>

<h2>10. Security</h2>
<p>We use HTTPS/TLS for all traffic, encrypt data at rest, hash passwords with bcrypt, restrict admin access via role-based permissions, and follow secure-development practices (rate limiting, input validation, dependency auditing). No online service is 100% secure — please use a strong unique password and enable two-factor authentication when available.</p>

<h2>11. Children</h2>
<p>The Site is intended for users 18 and older and we do not knowingly collect personal data from children.</p>

<h2>12. Changes to this policy</h2>
<p>We may update this policy from time to time. Material changes will be announced via email or a Site banner at least 14 days before taking effect. The current version is always available at <a href="/privacy">/privacy</a>.</p>

<h2>13. Contact</h2>
<p>Data-protection queries: <a href="mailto:nagesh@dreamhomeengineers.com">nagesh@dreamhomeengineers.com</a><br/>
Postal: Dream Home Engineers, 21-205, Valli Nilayam, Balajinagar, Kukatpally, Hyderabad, Telangana 500072, India.</p>
`;

const Privacy = () => (
  <LegalPageShell
    slug="privacy"
    title="Privacy Policy"
    effectiveDate="21 July 2026"
    summary="How Dream Home Engineers collects, uses, and protects your personal data when you use the Dream Home Plans marketplace."
    defaultBody={DEFAULT_BODY}
    contactEmail="nagesh@dreamhomeengineers.com"
  />
);

export default Privacy;
